/**
 * What the borrower actually needs on the day — the question public calculators
 * answer worst, and the one that sends people to a closing table short.
 *
 * Three things go into it, and they are different kinds of number:
 *
 *   1. The down payment. The owner's own money, which they usually know.
 *   2. The SBA guaranty fee. Exact, from the published schedule, and it can
 *      either be paid in cash or financed into the loan — so both are shown,
 *      because financing it means a bigger loan and a bigger payment.
 *   3. Third-party closing costs. Appraisal, environmental screening, business
 *      valuation, title, legal. SBA sets no price for any of these, so they are
 *      RANGES, labelled as estimates and editable. What SBA does set is which
 *      ones apply and when a dearer version is required — those triggers are
 *      read from the SOP and are exact.
 *
 * Conflating 2 and 3 is how a cash-to-close figure ends up looking precise and
 * being wrong. They are kept apart in the output for that reason.
 */

import data from '../data/closing-costs.json';
import { round2 } from '../../src/core/rates.js';
import { upfrontFee } from './fee7a.js';
import { amortize } from './annuity.js';
import type { Citation, FeeInput, FeeResult } from './types.js';

export interface ClosingCostLine {
  key: string;
  label: string;
  /** Low and high of a market range; equal when a figure is exact. */
  low: number;
  high: number;
  /** Midpoint, used for the headline figure. */
  likely: number;
  /** True when this is a market estimate rather than a published figure. */
  estimated: boolean;
  /** Why this cost applies, in the owner's terms. */
  why: string;
  /** Set when the owner supplied their own figure. */
  overridden?: boolean;
}

export interface CashToCloseInput extends FeeInput {
  /** Total project cost, when it differs from the loan plus the down payment. */
  projectCost?: number;
  /** The owner's own money going in. */
  equityInjection?: number;
  /** Any part of the money buying or improving real estate. */
  includesRealEstate?: boolean;
  isChangeOfOwnership?: boolean;
  /** Needed to tell a lender-performed valuation from an independent one. */
  businessPurchasePrice?: number;
  /** A close relationship forces an independent valuation at any price. */
  closeRelationshipBuyerSeller?: boolean;
  /** Roll the guaranty fee into the loan instead of paying it at closing. */
  financeGuarantyFee?: boolean;
  /** The owner's own quotes, replacing a market range. */
  costOverrides?: Record<string, number>;
  /** Only needed for the payment comparison. */
  annualRatePct?: number;
}

export interface CashToCloseResult {
  /** The owner's own money. */
  equityInjection: number;
  /** The SBA guaranty fee, exact unless the schedule itself is provisional. */
  guarantyFee: number;
  guarantyFeeFinanced: boolean;
  guarantyFeeProvisional: boolean;
  /** The lender's own annual fee, carried through so it stays visible and separate. */
  lenderAnnualFee: FeeResult['lenderAnnualFee'];
  /** Third-party costs, itemised. */
  closingCosts: ClosingCostLine[];
  closingCostsLow: number;
  closingCostsHigh: number;
  /** What the borrower brings, as a range because the costs are ranges. */
  cashAtClosingLow: number;
  cashAtClosingLikely: number;
  cashAtClosingHigh: number;
  /** Loan plus the fee, when the fee is financed. */
  amountBorrowed: number;
  /** Both options side by side, when a rate was supplied. */
  comparison: {
    payCashMonthlyPayment: number;
    financeFeeMonthlyPayment: number;
    financeFeeExtraPerMonth: number;
    financeFeeExtraTotalInterest: number;
  } | null;
  notes: string[];
  citations: { guarantyFee: Citation; passThrough: Citation };
}

interface RawItem {
  key: string;
  label: string;
  appliesWhen: 'real-estate' | 'change-of-ownership' | 'always';
  loanAmountAtOrBelow?: number;
  loanAmountAbove?: number;
  purchasePriceAtOrBelow?: number;
  purchasePriceAbove?: number;
  marketRange: boolean;
  verified: boolean;
  low: number;
  high: number;
  why: string;
}

const D = data as unknown as {
  passThroughRule: { source: string; citation: string; asOf: string; verified: boolean };
  items: RawItem[];
};

const PASS_THROUGH: Citation = {
  source: D.passThroughRule.source,
  citation: D.passThroughRule.citation,
  asOf: D.passThroughRule.asOf,
  verified: D.passThroughRule.verified,
};

const MONEY = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

/** Which third-party costs this deal triggers. */
function applicableItems(input: CashToCloseInput): RawItem[] {
  const price = input.businessPurchasePrice ?? 0;
  // A close relationship removes the lender-performed option at any price.
  const forcesIndependent = input.closeRelationshipBuyerSeller === true;

  return D.items.filter((item) => {
    if (item.appliesWhen === 'real-estate' && !input.includesRealEstate) return false;
    if (item.appliesWhen === 'change-of-ownership' && !input.isChangeOfOwnership) return false;

    if (item.loanAmountAtOrBelow !== undefined && input.grossAmount > item.loanAmountAtOrBelow) {
      return false;
    }
    if (item.loanAmountAbove !== undefined && input.grossAmount <= item.loanAmountAbove) {
      return false;
    }
    if (item.purchasePriceAtOrBelow !== undefined) {
      if (forcesIndependent || price > item.purchasePriceAtOrBelow) return false;
    }
    if (item.purchasePriceAbove !== undefined) {
      if (!forcesIndependent && price <= item.purchasePriceAbove) return false;
    }
    return true;
  });
}

/**
 * Cash the borrower needs at closing, itemised, with the financed-fee
 * alternative worked out beside it.
 */
export function cashToClose(input: CashToCloseInput): CashToCloseResult {
  const fee: FeeResult = upfrontFee(input);
  const notes: string[] = [...fee.notes];

  const lines: ClosingCostLine[] = applicableItems(input).map((item) => {
    const override = input.costOverrides?.[item.key];
    if (override !== undefined) {
      if (!Number.isFinite(override) || override < 0) {
        throw new Error(`Cost override for "${item.key}" must be zero or more`);
      }
      return {
        key: item.key,
        label: item.label,
        low: round2(override),
        high: round2(override),
        likely: round2(override),
        estimated: false,
        why: item.why,
        overridden: true,
      };
    }
    return {
      key: item.key,
      label: item.label,
      low: item.low,
      high: item.high,
      likely: round2((item.low + item.high) / 2),
      estimated: item.marketRange,
      why: item.why,
    };
  });

  const closingCostsLow = round2(lines.reduce((s, l) => s + l.low, 0));
  const closingCostsHigh = round2(lines.reduce((s, l) => s + l.high, 0));
  const closingCostsLikely = round2(lines.reduce((s, l) => s + l.likely, 0));

  const equityInjection = Math.max(input.equityInjection ?? 0, 0);
  const financed = input.financeGuarantyFee === true;
  const feeAtClosing = financed ? 0 : fee.upfrontFee;
  const amountBorrowed = round2(input.grossAmount + (financed ? fee.upfrontFee : 0));

  const base = equityInjection + feeAtClosing;
  const result: Omit<CashToCloseResult, 'comparison' | 'notes'> = {
    equityInjection,
    guarantyFee: fee.upfrontFee,
    guarantyFeeFinanced: financed,
    guarantyFeeProvisional: fee.provisional,
    lenderAnnualFee: fee.lenderAnnualFee,
    closingCosts: lines,
    closingCostsLow,
    closingCostsHigh,
    cashAtClosingLow: round2(base + closingCostsLow),
    cashAtClosingLikely: round2(base + closingCostsLikely),
    cashAtClosingHigh: round2(base + closingCostsHigh),
    amountBorrowed,
    citations: { guarantyFee: fee.citation, passThrough: PASS_THROUGH },
  };

  if (lines.some((l) => l.estimated)) {
    notes.push(
      `SBA does not set prices for appraisals, environmental reports, valuations or legal work, so those lines are ranges based on what they typically cost. Put your own quotes in as you get them and this figure gets exact.`,
    );
  }
  if (lines.length > 0) {
    notes.push(
      `Your lender has to itemise these costs for you in writing, so ask for the list before closing.`,
    );
  }
  if (financed) {
    notes.push(
      `The ${MONEY(
        fee.upfrontFee,
      )} guaranty fee is financed, so you borrow ${MONEY(amountBorrowed)} instead of ${MONEY(
        input.grossAmount,
      )} and keep that cash at closing — but you pay interest on it for the life of the loan.`,
    );
  } else if (fee.upfrontFee > 0) {
    notes.push(
      `The ${MONEY(fee.upfrontFee)} guaranty fee is paid at closing here. Most lenders will let you finance it instead.`,
    );
  }

  // Both options, when we know the rate.
  let comparison: CashToCloseResult['comparison'] = null;
  if (input.annualRatePct !== undefined && input.annualRatePct >= 0) {
    const cash = amortize(input.grossAmount, input.annualRatePct, input.maturityMonths);
    const rolled = amortize(
      round2(input.grossAmount + fee.upfrontFee),
      input.annualRatePct,
      input.maturityMonths,
    );
    comparison = {
      payCashMonthlyPayment: cash.payment,
      financeFeeMonthlyPayment: rolled.payment,
      financeFeeExtraPerMonth: round2(rolled.payment - cash.payment),
      financeFeeExtraTotalInterest: round2(rolled.totalInterest - cash.totalInterest),
    };
    if (fee.upfrontFee > 0) {
      notes.push(
        `Financing the fee adds ${MONEY(
          comparison.financeFeeExtraPerMonth,
        )} a month and ${MONEY(
          comparison.financeFeeExtraTotalInterest,
        )} in interest over the full term.`,
      );
    }
  }

  return { ...result, comparison, notes };
}
